import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Spinner from "../../Atoms/Spinner";
import Section from "../../Atoms/Section";
import H1 from "../../Atoms/H1";
import * as api from "../../../api";
import EnhancedTable from "../../Components/EnhancedTable";
import CSVExportButton from "../../Components/CSVExportButton";
import BlueButton from "../../Atoms/BlueButton";
import * as XLSX from 'xlsx';

const FD100TalkRSVP = (props) => {
    const [rsvps, setRsvps] = useState(null);
    const [uniqueRsvps, setUniqueRsvps] = useState(null);
    const [tableData, setTableData] = useState(null);
    const [approvalMap, setApprovalMap] = useState({});
    const navigate = useNavigate();

    useEffect(() => {
        if (props.fullUserLoading) return;
        if (!props.fullUser?.user) navigate("/");
        if (props.fullUser?.user && props.fullUser.user.role !== "admin") navigate("/");
    }, [props.fullUserLoading, props.fullUser, navigate]);

    useEffect(() => {
        api.getAllRSVPTalks().then(res => {
            setRsvps(res.data);
        }).catch(console.error);
    }, []);

    // Deduplicate and store unique list
    useEffect(() => {
        if (!rsvps) return;

        const seen = new Set();
        const unique = rsvps
            .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
            .filter(r => {
                const key = `${(r.first_name || '').toLowerCase().trim()}|${(r.last_name || '').toLowerCase().trim()}|${(r.email || '').toLowerCase().trim()}`;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            });

        setUniqueRsvps(unique);

        // Init approval map from DB values
        const aMap = {};
        unique.forEach(r => {
            aMap[r.id] = !!r.approved;
        });
        setApprovalMap(aMap);
    }, [rsvps]);

    const toggleApproval = useCallback((rsvpId) => {
        const newValue = !approvalMap[rsvpId];
        setApprovalMap(prev => ({ ...prev, [rsvpId]: newValue }));
        api.updateRSVPTalkApproval({ id: rsvpId, approved: newValue }).catch(err => {
            console.error(err);
            // Revert on error
            setApprovalMap(prev => ({ ...prev, [rsvpId]: !newValue }));
        });
    }, [approvalMap]);

    // Build table data whenever unique list or approval map changes
    useEffect(() => {
        if (!uniqueRsvps) return;

        const _tableData = [];
        _tableData.push(["Prénom", "Nom", "Société", "Email", "Date d'inscription", "Approuvé"]);

        for (let i = 0; i < uniqueRsvps.length; i++) {
            const r = uniqueRsvps[i];
            const date = new Date(r.created_at);
            const formattedDate = date.toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });

            _tableData.push([
                r.first_name,
                r.last_name,
                r.company || '',
                r.email,
                formattedDate,
                {
                    button: true,
                    label: approvalMap[r.id] ? '✓ Approuvé' : '—',
                    onClick: r.id
                }
            ]);
        }
        setTableData(_tableData);
    }, [uniqueRsvps, approvalMap]);

    const handleButtonClick = useCallback((rsvpId) => {
        toggleApproval(rsvpId);
    }, [toggleApproval]);

    // Export data (without React elements) for CSV/XLSX
    const getExportData = useCallback(() => {
        if (!uniqueRsvps) return [];
        const rows = [["Prénom", "Nom", "Société", "Email", "Date d'inscription", "Approuvé"]];
        for (const r of uniqueRsvps) {
            const date = new Date(r.created_at);
            const formattedDate = date.toLocaleDateString('fr-FR', {
                day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
            });
            rows.push([
                r.first_name,
                r.last_name,
                r.company || '',
                r.email,
                formattedDate,
                approvalMap[r.id] ? 'Oui' : 'Non'
            ]);
        }
        return rows;
    }, [uniqueRsvps, approvalMap]);

    if (!tableData) return <Section className="padding"><Spinner /></Section>;

    const exportData = getExportData();

    return (
        <>
            <Section className="padding flexblock">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <H1>RSVP Table Ronde FD100 ({tableData.length - 1})</H1>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <CSVExportButton data={exportData} filename="fd100_talk_rsvp.csv" />
                        <BlueButton onClick={() => {
                            const data = getExportData();
                            const ws = XLSX.utils.aoa_to_sheet(data);
                            ws['!cols'] = data[0].map(() => ({ wch: 25 }));
                            const wb = XLSX.utils.book_new();
                            XLSX.utils.book_append_sheet(wb, ws, 'RSVP Talk');
                            XLSX.writeFile(wb, 'fd100_talk_rsvp.xlsx');
                        }}>Télécharger au format XLSX</BlueButton>
                    </div>
                </div>
            </Section>
            <Section>
                <EnhancedTable data={tableData} onButtonClick={handleButtonClick} />
            </Section>
        </>
    );
};

export default FD100TalkRSVP;
